import { Duration } from 'luxon'
import moment from 'moment'
import { inflect } from 'inflection'
import _ from 'lodash'

function formatMillisAsClockDuration (millis) {
  const sign = millis < 0 ? '-' : ''
  millis = Math.abs(millis)
  const totalMinutes = Math.round(millis / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}


function formatMillisAsDecimal (millis, decimalPlaces) {
  const negative = millis < 0
  millis = Math.abs(millis)
  const hours = Math.floor(millis / (60 * 60 * 1000))
  const minutes = millis / 60000 % 60
  const duration = _.round(hours + minutes / 60, decimalPlaces)
  const sign = negative && duration ? '-' : ''
  return `${sign}${duration.toFixed(decimalPlaces)}`
}

/**
 * Formats milliseconds as a day duration
 * By default full day duration is 24 hours, but can be overridden with overrideMillisPerDay parameter.
 * @param: millis (number to convert)
 * @param: decimalPlaces (number)
 * @param: overrideMillisPerDay (number, e.g., 28800000 for 8 hours)
 *        - overrides the default full day duration of 24 hours which is useful for time off display
 *          where it follows the time off defaults full day duration settings instead of 24 hours.
 * @returns: formatted string (e.g., "1.5 days", "1.25 days")
 */
function formatMillisAsDayDecimalUnit (millis, decimalPlaces, overrideMillisPerDay = undefined, isShortFormat = false) {
  millis = Number(millis || 0)
  const millisPerDay = overrideMillisPerDay
    ? Number(overrideMillisPerDay)
    : 24 * 60 * 60 * 1000 // default to 24 hours in milliseconds
  if (!Number.isFinite(millis) || !Number.isFinite(millisPerDay) || millisPerDay <= 0) return ''
  const negative = millis < 0
  const duration = _.round(Math.abs(millis) / millisPerDay, decimalPlaces)
  const sign = negative && duration ? '-' : ''
  const formattedDuration = duration.toFixed(decimalPlaces).replace(/\.?0+$/, '')
  return isShortFormat
    ? `${sign}${formattedDuration}d`
    : `${sign}${formattedDuration} ${inflect('day', duration)}`
}

/**
 * Formats milliseconds as a duration with units 
 * @param: milliseconds (number, e.g., 33000000)
 * @returns: formatted string (e.g., "9h 10m", "9h", "12m", "")
 */
function formatMillisAsUnits (millis) {
  const sign = millis < 0 ? '-' : ''
  const duration = Duration.fromMillis(Math.abs(millis)).shiftTo('hours', 'minutes')
  const hours = duration.hours
  const minutes = Math.round(duration.minutes)
  const u = []
  if (hours) {
    u.push(`${hours}h`)
  }
  if (minutes) {
    u.push(`${minutes}m`)
  }
  return u.length
    ? sign + u.join(' ')
    : ''
}

export function formatIsoDuration (isoDuration) {
  if (!isoDuration) return ''
  const duration = moment.duration(isoDuration)
  const units = ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds']
  const formatted = units.flatMap(unit => {
    const cardinality = duration[unit]()
    return cardinality ? [`${cardinality} ${inflect(unit, cardinality)}`] : []
  }).join(' ')
  return formatted || ''
}


export default {
  formatMillisAsClockDuration,
  formatMillisAsDecimal,
  formatMillisAsDayDecimalUnit,
  formatMillisAsUnits
}
